import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { formatDateShort, getOpenRateBadgeColor } from "../lib/utils";
import { 
  Plus, Users, Eye, MousePointerClick, Calendar, Trash2, ArrowRight, 
  Copy, Loader2, Archive, FileEdit, Send, BarChart3 
} from "lucide-react";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    bozza: { label: "Bozza", className: "bg-slate-100 text-slate-700" },
    attiva: { label: "Attiva", className: "bg-green-100 text-green-700" },
    archiviata: { label: "Archiviata", className: "bg-amber-100 text-amber-700" },
  };
  
  const config = statusConfig[status] || statusConfig.bozza;
  
  return (
    <Badge className={`${config.className} text-xs`} data-testid="status-badge">
      {config.label}
    </Badge>
  );
};

const OpenRateBadge = ({ rate }) => {
  const color = getOpenRateBadgeColor(rate);
  const colorClasses = {
    success: "bg-green-100 text-green-700 border-green-200",
    warning: "bg-amber-100 text-amber-700 border-amber-200",
    danger: "bg-red-100 text-red-700 border-red-200",
  };

  return (
    <Badge className={`${colorClasses[color]} font-semibold`} data-testid="open-rate-badge">
      {rate}%
    </Badge>
  );
};

const CampaignCard = ({ campaign, onDelete, onDuplicate, isDuplicating }) => (
  <Card className="bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 group">
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-heading font-semibold text-lg truncate" data-testid="campaign-name">
              {campaign.name}
            </h3>
            <StatusBadge status={campaign.status} />
          </div>
          <p className="text-sm text-muted-foreground truncate" data-testid="campaign-subject">
            {campaign.subject}
          </p>
        </div>
        <OpenRateBadge rate={campaign.openRate} />
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <Users className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold tabular-nums">{campaign.recipientCount}</p>
          <p className="text-xs text-muted-foreground">Destinatari</p>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <Eye className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold tabular-nums">{campaign.openCount}</p>
          <p className="text-xs text-muted-foreground">Aperture</p>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <MousePointerClick className="h-4 w-4" />
          </div>
          <p className="text-lg font-semibold tabular-nums">{campaign.clickCount}</p>
          <p className="text-xs text-muted-foreground">Click</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          {formatDateShort(campaign.createdAt)}
        </div>
        <div className="flex items-center gap-1">
          {/* Duplicate Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-primary"
            onClick={() => onDuplicate(campaign.id)}
            disabled={isDuplicating}
            data-testid="duplicate-campaign-btn"
            title="Duplica campagna"
          >
            {isDuplicating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
          
          {/* Delete Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                data-testid="delete-campaign-btn"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Elimina campagna</AlertDialogTitle>
                <AlertDialogDescription>
                  Sei sicuro di voler eliminare "{campaign.name}"? Questa azione eliminerà anche tutti i
                  destinatari, eventi e link associati.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(campaign.id)}
                  className="bg-destructive hover:bg-destructive/90"
                  data-testid="confirm-delete-btn"
                >
                  Elimina
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          
          {/* View Details Button */}
          <Link to={`/campaigns/${campaign.id}`}>
            <Button variant="ghost" size="sm" className="gap-2" data-testid="view-campaign-btn">
              Dettagli
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </CardContent>
  </Card>
);

const CampaignCardSkeleton = () => (
  <Card className="bg-white border border-slate-100">
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Skeleton className="h-6 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-6 w-12" />
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <Skeleton className="h-4 w-24" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-20" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const EmptyState = () => (
  <div className="col-span-full">
    <Card className="bg-white border border-slate-100 shadow-sm">
      <CardContent className="p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Send className="h-10 w-10 text-primary" />
          </div>
          <h3 className="font-heading font-semibold text-xl mb-2">Nessuna campagna</h3>
          <p className="text-muted-foreground mb-6">
            Crea la tua prima campagna per iniziare a tracciare le aperture delle tue email.
          </p>
          <Link to="/campaigns/new">
            <Button className="gap-2 bg-primary hover:bg-primary-hover" data-testid="empty-create-campaign-btn">
              <Plus className="h-4 w-4" />
              Crea campagna
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  </div>
);

const CampaignsPage = () => {
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns", showArchived],
    queryFn: async () => {
      const response = await api.get(`/campaigns?includeArchived=${showArchived}`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (campaignId) => api.delete(`/campaigns/${campaignId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campagna eliminata con successo");
    },
    onError: () => {
      toast.error("Errore durante l'eliminazione della campagna");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (campaignId) => api.post(`/campaigns/${campaignId}/duplicate`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success(`Campagna duplicata! ${data.data.recipientsCopied} destinatari copiati.`);
      setDuplicatingId(null);
    },
    onError: () => {
      toast.error("Errore durante la duplicazione della campagna");
      setDuplicatingId(null);
    },
  });

  const handleDuplicate = (campaignId) => {
    setDuplicatingId(campaignId);
    duplicateMutation.mutate(campaignId);
  };

  // Separate active and archived campaigns
  const activeCampaigns = campaigns?.filter(c => c.status !== "archiviata") || [];
  const archivedCampaigns = campaigns?.filter(c => c.status === "archiviata") || [];

  return (
    <div className="space-y-8" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading">Campagne</h1>
          <p className="text-muted-foreground">Gestisci le tue campagne di email tracking</p>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/campaigns/compare">
            <Button variant="outline" className="gap-2" data-testid="compare-campaigns-btn">
              <BarChart3 className="h-4 w-4" />
              Confronta
            </Button>
          </Link>
          <Link to="/campaigns/new">
            <Button className="gap-2 bg-primary hover:bg-primary-hover" data-testid="create-campaign-btn">
              <Plus className="h-4 w-4" />
              Nuova Campagna
            </Button>
          </Link>
        </div>
      </div>

      {/* Archive Toggle */}
      <div className="flex items-center gap-3">
        <Switch
          id="show-archived"
          checked={showArchived}
          onCheckedChange={setShowArchived}
          data-testid="show-archived-toggle"
        />
        <Label htmlFor="show-archived" className="text-sm text-muted-foreground flex items-center gap-2">
          <Archive className="h-4 w-4" />
          Mostra archiviate ({archivedCampaigns.length})
        </Label>
      </div>

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => <CampaignCardSkeleton key={i} />)
        ) : campaigns?.length > 0 ? (
          campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onDelete={deleteMutation.mutate}
              onDuplicate={handleDuplicate}
              isDuplicating={duplicatingId === campaign.id}
            />
          ))
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

export default CampaignsPage;
